/**
 * Topics (Kafka), a small kafka-ui: the project's topics (its prefix hidden; an app's unprefixed one marked) with
 * partitions, messages and the time of the newest; the consumer groups of all of them; and for the topic picked an
 * overview (messages in the log, end offset, committed, still to consume) and tabs: Messages (newest first, which
 * groups consumed each), Consumers (lag per group and partition, the last message consumed, resets), Partitions
 * (watermarks, leader, retention) and Produce. Everything refreshes every 5 s while the page is visible (pausable).
 * Viewers see it all and change nothing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Pause, Play, Plus } from '@phosphor-icons/react';
import { ConfirmDialog, DataDialog, EmptyState, ErrorState, Loading, ReadOnlyBadge, errorText } from './DataCommon';
import { createTopic, deleteTopic, getTopicConsumers, getTopicPartitions, listTopics, produceMessage } from '../../services/DataService';
import { ago, formatCount, kafkaQuery } from '../../utils/dataModel';
import KafkaMessages from './KafkaMessages';
import { GroupsList, TopicConsumers } from './KafkaConsumers';
import { PartitionsTab, ProduceForm } from './KafkaParts';
import { plural, topicKey, useAutoRefresh } from './kafkaModel';

const GROUPS = '__groups';
const LAST_EVERY = 6; // ticks: the newest message's time is read every 30 s, not every 5 s

/** The topic a name from the rail means: of two with that name (prefixed and unprefixed) the one with messages. */
function pick(list, name) {
  const same = list.filter((t) => t.name === name);
  if (!same.length) { return null; }
  return [...same].sort((a, b) => (Number(b.messages) || 0) - (Number(a.messages) || 0) || (a.unprefixed ? 1 : 0) - (b.unprefixed ? 1 : 0))[0];
}

function Stat({ label, value, hint = null, tone = '' }) {
  return (
    <div className={`data-kafka-stat ${tone}`} title={hint || undefined}>
      <span className="data-muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

Stat.propTypes = { hint: PropTypes.string, label: PropTypes.string.isRequired, tone: PropTypes.string, value: PropTypes.node.isRequired };

function Overview({ topic, consumers }) {
  const groups = consumers?.groups || [];
  const lag = consumers?.lag;
  const committed = groups.length === 1 ? formatCount(groups[0].committed) : groups.length ? `${groups.length} groups` : '—';
  return (
    <div aria-label="Topic overview" className="data-kafka-stats" role="group">
      <Stat hint="High minus low watermark, summed over the partitions" label="In the log" value={topic.messages === null || topic.messages === undefined ? '?' : formatCount(topic.messages)} />
      <Stat hint="The offset the next message gets, summed over the partitions" label="End offset" value={formatCount(consumers?.end ?? topic.end) || '—'} />
      <Stat hint={groups.map((g) => `${g.groupId}: ${formatCount(g.committed)}`).join('\n') || 'No consumer group'} label="Committed" value={committed} />
      <Stat hint="Messages the groups have not consumed yet (summed over the groups)" label="To be consumed" tone={lag ? 'is-warn' : ''} value={lag === null || lag === undefined ? '—' : formatCount(lag)} />
      <Stat label="Last message" value={topic.lastMessageAt ? ago(topic.lastMessageAt) : '—'} />
    </div>
  );
}

Overview.propTypes = { consumers: PropTypes.object, topic: PropTypes.object.isRequired };

export default function KafkaView({ projectId, store, topic: initialTopic = null, readOnly = false, onTopicsChanged, onOpenQuery }) {
  const [topics, setTopics] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('messages');
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0);
  const [consumers, setConsumers] = useState(null);
  const [consumersError, setConsumersError] = useState(null);
  const [partitions, setPartitions] = useState(null);
  const [partitionsError, setPartitionsError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPartitions, setNewPartitions] = useState('1');
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canWrite = !readOnly && !store.readOnly;
  const lastTimes = useRef({});

  const loadTopics = useCallback(async ({ withLast = false } = {}) => {
    setError(null);
    try {
      const result = await listTopics(projectId, { withLast });
      const list = (result.topics || []).map((t) => {
        // keep the newest message's time between the reads of it
        if (t.lastMessageAt) { lastTimes.current[topicKey(t)] = t.lastMessageAt; }
        return t.lastMessageAt || !lastTimes.current[topicKey(t)] ? t : { ...t, lastMessageAt: lastTimes.current[topicKey(t)] };
      });
      setTopics(list);
      return list;
    } catch (e) {
      setError(errorText(e));
      return null;
    }
  }, [projectId]);

  useEffect(() => {
    loadTopics({ withLast: true }).then((list) => {
      if (!list || !list.length) { return; }
      const first = (initialTopic && pick(list, initialTopic)) || list[0];
      setSelected((prev) => prev || topicKey(first));
    });
  }, [loadTopics]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = useMemo(() => (topics && selected !== GROUPS ? topics.find((t) => topicKey(t) === selected) || null : null), [topics, selected]);
  const ref = useMemo(() => (current ? { name: current.name, topic: current.topic } : null), [current?.name, current?.topic]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConsumers = useCallback(async (last) => {
    if (!ref) { return; }
    try {
      const out = await getTopicConsumers(projectId, ref, { last });
      setConsumers((prev) => (last || !prev ? out : {
        ...out, groups: out.groups.map((g) => ({ ...g, lastConsumed: prev.groups?.find((p) => p.groupId === g.groupId)?.lastConsumed })),
      }));
      setConsumersError(null);
    } catch (e) {
      setConsumersError(errorText(e));
    }
  }, [projectId, ref]);

  const loadPartitions = useCallback(async () => {
    if (!ref) { return; }
    try {
      setPartitions(await getTopicPartitions(projectId, ref));
      setPartitionsError(null);
    } catch (e) {
      setPartitionsError(errorText(e));
    }
  }, [projectId, ref]);

  useEffect(() => { setConsumers(null); setPartitions(null); setConsumersError(null); setPartitionsError(null); }, [ref]);
  useEffect(() => { loadConsumers(tab === 'consumers'); }, [loadConsumers, tab]);
  useEffect(() => { if (tab === 'partitions') { loadPartitions(); } }, [loadPartitions, tab]);

  useAutoRefresh(() => {
    const next = tick + 1;
    setTick(next);
    loadTopics({ withLast: next % LAST_EVERY === 0 });
    if (ref) {
      loadConsumers(tab === 'consumers');
      if (tab === 'partitions') { loadPartitions(); }
    }
  }, { on: !paused && Boolean(topics?.length) });

  const create = async () => {
    const name = newName.trim();
    if (!name) { return; }
    setBusy(true);
    setDialogError(null);
    try {
      const out = await createTopic(projectId, { name, partitions: Math.max(1, Number(newPartitions) || 1) });
      setCreating(false);
      setNewName('');
      await loadTopics();
      setSelected(out?.topic || name);
      onTopicsChanged?.();
    } catch (e) {
      setDialogError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setDialogError(null);
    try {
      await deleteTopic(projectId, ref);
      setConfirmDelete(false);
      const list = await loadTopics();
      setSelected(list?.[0] ? topicKey(list[0]) : null);
      onTopicsChanged?.();
    } catch (e) {
      setDialogError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const send = async (message) => {
    const out = await produceMessage(projectId, ref, message);
    loadTopics();
    return out;
  };

  const tabs = [['messages', 'Messages'], ['consumers', 'Consumers'], ['partitions', 'Partitions'], ...(canWrite ? [['produce', 'Produce']] : [])];
  const title = selected === GROUPS ? 'Consumer groups' : current?.name || 'Topics';

  return (
    <div className="data-view" data-testid="kafka-view">
      <div className="data-view-head">
        <h1>{title}</h1>
        {current && <span className="data-pill">{plural(current.partitions, 'partition')}</span>}
        {current?.messages !== undefined && current?.messages !== null && <span className="data-pill">{formatCount(current.messages)} messages</span>}
        {current?.unprefixed && <span className="data-pill is-warn" title={`The real topic is "${current.topic}": an app created it without the project's prefix`}>unprefixed</span>}
        {!canWrite && <ReadOnlyBadge reason={readOnly ? 'You are a viewer of this project' : 'This store is read-only here'} />}
        <span className="data-grow" />
        {topics?.length > 0 && (
          <button aria-pressed={!paused} className="data-btn" onClick={() => setPaused((p) => !p)} title="Refresh every 5 seconds" type="button">
            {paused ? <Play aria-hidden size={15} /> : <Pause aria-hidden size={15} />}{paused ? 'Auto-refresh paused' : 'Auto-refresh'}
          </button>
        )}
        {current && onOpenQuery && <button className="data-btn" onClick={() => onOpenQuery(kafkaQuery(current, topics))} title="Find messages of this topic by their key, value fields or time" type="button">Query</button>}
        {canWrite && <button className="data-btn" onClick={() => { setDialogError(null); setCreating(true); }} type="button"><Plus aria-hidden size={15} />Topic</button>}
        {canWrite && current && <button className="data-btn is-danger" onClick={() => { setDialogError(null); setConfirmDelete(true); }} type="button">Delete topic…</button>}
      </div>

      {error && <ErrorState error={error} onRetry={loadTopics} />}
      {!error && !topics && <Loading label="Loading the topics…" />}
      {topics && !topics.length && (
        <EmptyState
          action={canWrite ? <button className="data-btn is-primary" onClick={() => setCreating(true)} type="button"><Plus aria-hidden size={15} />Create a topic</button> : null}
          title="No topics yet"
        >
          A service that produces to a topic creates it; you can also create one here.
        </EmptyState>
      )}

      {topics && topics.length > 0 && (
        <div className="data-split">
          <nav aria-label="Topics" className="data-card data-topics">
            <ul className="data-plain-list">
              <li>
                <button aria-current={selected === GROUPS ? 'true' : undefined} className={`data-kv-btn${selected === GROUPS ? ' is-on' : ''}`} onClick={() => setSelected(GROUPS)} type="button">
                  <span>All consumer groups</span>
                </button>
              </li>
              {topics.map((t) => {
                const key = topicKey(t);
                const on = key === selected;
                const count = t.messages === null || t.messages === undefined ? '?' : formatCount(t.messages);
                return (
                  <li key={key}>
                    <button aria-current={on ? 'true' : undefined} className={`data-kv-btn${on ? ' is-on' : ''}`} onClick={() => setSelected(key)} title={t.error || (t.unprefixed ? `${t.topic} (no project prefix)` : t.topic)} type="button">
                      <span className="data-mono">{t.name}{t.unprefixed ? <span className="data-muted"> · unprefixed</span> : null}</span>
                      <span className="data-muted">{t.partitions} part. · {count}{t.lastMessageAt ? ` · ${ago(t.lastMessageAt)}` : ''}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="data-stack">
            {selected === GROUPS && (
              <GroupsList canWrite={canWrite} onOpenTopic={(t) => { setSelected(topicKey(t)); setTab('consumers'); }} projectId={projectId} refreshKey={tick} />
            )}
            {current && (
              <>
                <Overview consumers={consumers} topic={current} />
                <div aria-label="Topic views" className="data-seg data-kafka-tabs" role="tablist">
                  {tabs.map(([id, label]) => (
                    <button aria-selected={tab === id} key={id} onClick={() => setTab(id)} role="tab" type="button">{label}</button>
                  ))}
                </div>
                {tab === 'messages' && <KafkaMessages canWrite={canWrite} consumers={consumers} key={selected} projectId={projectId} refreshKey={Number(current.end) || 0} topic={current} />}
                {tab === 'consumers' && (
                  <TopicConsumers
                    canWrite={canWrite}
                    consumers={consumers}
                    error={consumersError}
                    onChanged={() => loadConsumers(true)}
                    onRetry={() => loadConsumers(true)}
                    projectId={projectId}
                    topic={current}
                  />
                )}
                {tab === 'partitions' && <PartitionsTab error={partitionsError} onRetry={loadPartitions} partitions={partitions} />}
                {tab === 'produce' && canWrite && <ProduceForm key={selected} onSend={send} />}
              </>
            )}
          </div>
        </div>
      )}

      {creating && (
        <DataDialog
          footer={(
            <>
              <span className="data-grow" />
              <button className="data-btn" onClick={() => setCreating(false)} type="button">Cancel</button>
              <button className="data-btn is-primary" disabled={busy || !newName.trim()} onClick={create} type="button">{busy ? 'Creating…' : 'Create topic'}</button>
            </>
          )}
          onClose={() => setCreating(false)}
          title="New topic"
        >
          <form className="data-form-grid" onSubmit={(e) => { e.preventDefault(); create(); }}>
            <label className="data-label" htmlFor="data-topic-name">Name</label>
            <input className="data-input data-mono" data-autofocus id="data-topic-name" onChange={(e) => setNewName(e.target.value)} placeholder="orders" value={newName} />
            <label className="data-label" htmlFor="data-topic-partitions">Partitions</label>
            <input className="data-input is-narrow" id="data-topic-partitions" inputMode="numeric" min={1} onChange={(e) => setNewPartitions(e.target.value)} type="number" value={newPartitions} />
          </form>
          {dialogError && <p className="data-error-text" role="alert">{dialogError}</p>}
        </DataDialog>
      )}
      {confirmDelete && current && (
        <ConfirmDialog busy={busy} error={dialogError} onClose={() => setConfirmDelete(false)} onConfirm={remove} title={`Delete the topic ${current.name}?`}>
          <p>The topic and all its messages go for good. A service that produces to it may create it again.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

KafkaView.propTypes = {
  onOpenQuery: PropTypes.func,
  onTopicsChanged: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  store: PropTypes.object.isRequired,
  topic: PropTypes.string,
};
